/**
 * Hook to load compliance data from Medplum in live mode, or local mock data in mock mode.
 *
 * FHIR model:
 *   Licenses:       Practitioner.qualification (native)
 *   Overtime/Admin:  Observation with COMPLIANCE_OBS_CODES
 *   Note Quality:    Observation with NOTE_QUALITY_OBS_CODES
 */

import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Observation, Practitioner } from '@medplum/fhirtypes';
import type {
  AdminHourOverageRow,
  DailyHourTotalRow,
  LicenseRow,
  LicenseStatus,
  MonthlyHourTotalRow,
  MonthsFullTimeRow,
  NoteQualityRow,
  NoteQualityWarningType,
  Supervisee,
  WeeklyHourTotalRow,
} from '../types';
import {
  COMPLIANCE_OBS_CATEGORY,
  COMPLIANCE_OBSERVATION_SYSTEM,
  COMPLIANCE_OBS_CODES,
  COMPLIANCE_COMPONENT_CODES,
  NOTE_QUALITY_OBS_CATEGORY,
  NOTE_QUALITY_OBSERVATION_SYSTEM,
  NOTE_QUALITY_OBS_CODES,
  NOTE_QUALITY_COMPONENT_CODES,
} from '../supervisionFhirConstants';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import { useSupervisionScope } from './useSupervisionScope';

function extractRefId(ref: string | undefined): string | undefined {
  if (!ref) return undefined;
  const match = ref.match(/\w+\/([^/]+)$/);
  return match?.[1];
}

function computeLicenseStatus(expirationDate: string): LicenseStatus {
  const exp = new Date(expirationDate);
  const now = new Date();
  if (exp < now) return 'expired';
  const ninetyDays = new Date();
  ninetyDays.setDate(ninetyDays.getDate() + 90);
  if (exp < ninetyDays) return 'expiring-soon';
  return 'active';
}

function practitionerToLicenseRows(practitioner: Practitioner): LicenseRow[] {
  if (!practitioner.qualification?.length) return [];
  const displayName = formatPractitionerStaffLabel(practitioner) || '—';

  // Only include qualifications that have a period.end (licensure), not specialties
  return practitioner.qualification
    .filter((qual) => qual.period?.end)
    .map((qual, idx) => {
      const code = qual.code?.coding?.[0]?.display ?? qual.code?.text ?? '—';
      const expiration = qual.period?.end ?? '';
      return {
        id: `${practitioner.id}-lic-${idx}`,
        providerId: practitioner.id!,
        providerName: displayName,
        highestLicense: code,
        licenseExpiration: expiration,
        status: expiration ? computeLicenseStatus(expiration) : 'active',
      };
    });
}

function getComponentValue(obs: Observation, componentCode: string): number | undefined {
  const comp = obs.component?.find(
    (c) => c.code?.coding?.some((coding) => coding.code === componentCode),
  );
  return comp?.valueQuantity?.value;
}

function obsToWeeklyHourRow(obs: Observation): WeeklyHourTotalRow | null {
  const practId = extractRefId(obs.subject?.reference);
  if (!practId) return null;
  return {
    id: obs.id!,
    providerId: practId,
    providerName: obs.subject?.display ?? '—',
    weekStart: (obs.effectivePeriod?.start ?? obs.effectiveDateTime ?? '').slice(0, 10),
    overtimeHours: getComponentValue(obs, COMPLIANCE_COMPONENT_CODES.overtimeHours.code) ?? 0,
  };
}

function obsToAdminOverageRow(obs: Observation): AdminHourOverageRow | null {
  const practId = extractRefId(obs.subject?.reference);
  if (!practId) return null;
  return {
    id: obs.id!,
    providerId: practId,
    providerName: obs.subject?.display ?? '—',
    weekStart: (obs.effectivePeriod?.start ?? obs.effectiveDateTime ?? '').slice(0, 10),
    adminRatio: getComponentValue(obs, COMPLIANCE_COMPONENT_CODES.adminRatio.code) ?? 0,
    adminHours: getComponentValue(obs, COMPLIANCE_COMPONENT_CODES.adminHours.code) ?? 0,
    clinicalHours: getComponentValue(obs, COMPLIANCE_COMPONENT_CODES.clinicalHours.code) ?? 0,
  };
}

function obsToDailyHourRow(obs: Observation): DailyHourTotalRow | null {
  const practId = extractRefId(obs.subject?.reference);
  if (!practId) return null;
  return {
    id: obs.id!,
    providerId: practId,
    providerName: obs.subject?.display ?? '—',
    date: (obs.effectiveDateTime ?? '').slice(0, 10),
    totalHours: getComponentValue(obs, COMPLIANCE_COMPONENT_CODES.totalHours.code) ?? obs.valueQuantity?.value ?? 0,
    adminHours: getComponentValue(obs, COMPLIANCE_COMPONENT_CODES.adminHours.code) ?? 0,
    overtimeHours: getComponentValue(obs, COMPLIANCE_COMPONENT_CODES.overtimeHours.code) ?? 0,
  };
}

function obsToMonthsFullTimeRow(obs: Observation): MonthsFullTimeRow | null {
  const practId = extractRefId(obs.subject?.reference);
  if (!practId) return null;
  return {
    id: obs.id!,
    providerId: practId,
    providerName: obs.subject?.display ?? '—',
    monthsOver130Hours: obs.valueQuantity?.value ?? obs.valueInteger ?? 0,
  };
}

function obsToMonthlyHourRow(obs: Observation): MonthlyHourTotalRow | null {
  const practId = extractRefId(obs.subject?.reference);
  if (!practId) return null;
  return {
    id: obs.id!,
    providerId: practId,
    providerName: obs.subject?.display ?? '—',
    monthStart: (obs.effectivePeriod?.start ?? obs.effectiveDateTime ?? '').slice(0, 10),
    totalHours: obs.valueQuantity?.value ?? 0,
  };
}

function obsToNoteQualityRow(obs: Observation): NoteQualityRow | null {
  const practId = extractRefId(obs.subject?.reference);
  if (!practId) return null;

  const warningCode = obs.code?.coding?.find((c) => c.system === NOTE_QUALITY_OBSERVATION_SYSTEM)?.code;
  let warningType: NoteQualityWarningType = 'edit-distance';
  if (warningCode === NOTE_QUALITY_OBS_CODES.adjustmentDisorder.code) {
    warningType = 'adjustment-disorder';
  }

  const editDistanceComp = obs.component?.find(
    (c) => c.code?.coding?.some((coding) => coding.code === NOTE_QUALITY_COMPONENT_CODES.editDistanceValue.code),
  );
  const editDistance = editDistanceComp?.valueQuantity?.value;

  const focusRef = obs.focus?.[0]?.reference;
  const patientDisplay = focusRef ? obs.focus?.[0]?.display : undefined;

  return {
    id: obs.id!,
    providerId: practId,
    providerName: obs.subject?.display ?? '—',
    clientId: undefined,
    clientName: patientDisplay ?? '—',
    sessionDate: (obs.effectiveDateTime ?? '').slice(0, 10),
    warningType,
    editDistance: editDistance != null ? editDistance : undefined,
  };
}

export interface UseComplianceResult {
  licenses: LicenseRow[];
  weeklyHours: WeeklyHourTotalRow[];
  adminOverages: AdminHourOverageRow[];
  dailyHours: DailyHourTotalRow[];
  monthsFullTime: MonthsFullTimeRow[];
  monthlyHours: MonthlyHourTotalRow[];
  noteQuality: NoteQualityRow[];
  supervisees: Supervisee[];
  providerOptions: Array<{ value: string; label: string }>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useComplianceFromMedplum(): UseComplianceResult {
  const medplum = useMedplum();
  const { showComplianceTab, scopeReady, isClinicianInScope } = useSupervisionScope();

  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [weeklyHours, setWeeklyHours] = useState<WeeklyHourTotalRow[]>([]);
  const [adminOverages, setAdminOverages] = useState<AdminHourOverageRow[]>([]);
  const [dailyHours, setDailyHours] = useState<DailyHourTotalRow[]>([]);
  const [monthsFullTime, setMonthsFullTime] = useState<MonthsFullTimeRow[]>([]);
  const [monthlyHours, setMonthlyHours] = useState<MonthlyHourTotalRow[]>([]);
  const [noteQuality, setNoteQuality] = useState<NoteQualityRow[]>([]);
  const [supervisees, setSupervisees] = useState<Supervisee[]>([]);
  const [providerOptions, setProviderOptions] = useState<Array<{ value: string; label: string }>>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!showComplianceTab) {
      setLicenses([]);
      setWeeklyHours([]);
      setAdminOverages([]);
      setDailyHours([]);
      setMonthsFullTime([]);
      setMonthlyHours([]);
      setNoteQuality([]);
      setSupervisees([]);
      setProviderOptions([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (!scopeReady) {
      setLoading(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const categoryCompliance = `${COMPLIANCE_OBS_CATEGORY.system}|${COMPLIANCE_OBS_CATEGORY.code}`;
      const categoryNoteQuality = `${NOTE_QUALITY_OBS_CATEGORY.system}|${NOTE_QUALITY_OBS_CATEGORY.code}`;

      const [practitioners, complianceObs, noteQualityObs] = await Promise.all([
        medplum.searchResources('Practitioner', { _count: '200' }),
        medplum.searchResources('Observation', {
          category: categoryCompliance,
          _count: '500',
          _sort: '-_lastUpdated',
        }),
        medplum.searchResources('Observation', {
          category: categoryNoteQuality,
          _count: '200',
          _sort: '-_lastUpdated',
        }),
      ]);

      const scopedPractitioners = practitioners.filter((p) => p.id && isClinicianInScope(p.id));
      const providerNameById = new Map(
        scopedPractitioners.map((p) => [
          p.id!,
          formatPractitionerStaffLabel(p) || p.id!,
        ]),
      );
      const withStaffNames = <T extends { providerId: string; providerName: string }>(
        rows: T[],
      ): T[] =>
        rows.map((row) => ({
          ...row,
          providerName: providerNameById.get(row.providerId) ?? row.providerName,
        }));

      // Licenses from Practitioner.qualification (supervisees only)
      const licenseRows = scopedPractitioners.flatMap(practitionerToLicenseRows);
      setLicenses(licenseRows);

      const provOpts = scopedPractitioners
        .filter((p) => p.active !== false)
        .map((p) => {
          const displayName = formatPractitionerStaffLabel(p) || p.id || '—';
          return { value: p.id!, label: displayName };
        });
      setProviderOptions(provOpts);

      const supList: Supervisee[] = provOpts.map((o) => ({
        id: o.value,
        name: o.label,
        role: 'Clinician',
        unsignedCount: 0,
        overdueCount: 0,
      }));
      setSupervisees(supList);

      const obsSubjectInScope = (obs: Observation): boolean => {
        const sid = extractRefId(obs.subject?.reference);
        return sid ? isClinicianInScope(sid) : false;
      };

      // Map compliance observations by code
      const weekly: WeeklyHourTotalRow[] = [];
      const admin: AdminHourOverageRow[] = [];
      const daily: DailyHourTotalRow[] = [];
      const monthsFT: MonthsFullTimeRow[] = [];
      const monthly: MonthlyHourTotalRow[] = [];

      for (const obs of complianceObs) {
        if (!obsSubjectInScope(obs)) continue;
        const code = obs.code?.coding?.find((c) => c.system === COMPLIANCE_OBSERVATION_SYSTEM)?.code;
        switch (code) {
          case COMPLIANCE_OBS_CODES.weeklyHours.code: {
            const row = obsToWeeklyHourRow(obs);
            if (row) weekly.push(row);
            break;
          }
          case COMPLIANCE_OBS_CODES.adminOverage.code: {
            const row = obsToAdminOverageRow(obs);
            if (row) admin.push(row);
            break;
          }
          case COMPLIANCE_OBS_CODES.dailyHours.code: {
            const row = obsToDailyHourRow(obs);
            if (row) daily.push(row);
            break;
          }
          case COMPLIANCE_OBS_CODES.monthsFullTime.code: {
            const row = obsToMonthsFullTimeRow(obs);
            if (row) monthsFT.push(row);
            break;
          }
          case COMPLIANCE_OBS_CODES.monthlyHours.code: {
            const row = obsToMonthlyHourRow(obs);
            if (row) monthly.push(row);
            break;
          }
        }
      }

      setWeeklyHours(withStaffNames(weekly));
      setAdminOverages(withStaffNames(admin));
      setDailyHours(withStaffNames(daily));
      setMonthsFullTime(withStaffNames(monthsFT));
      setMonthlyHours(withStaffNames(monthly));

      const nqRows = noteQualityObs
        .filter(obsSubjectInScope)
        .map(obsToNoteQualityRow)
        .filter((r): r is NoteQualityRow => r !== null);
      setNoteQuality(withStaffNames(nqRows));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [medplum, showComplianceTab, scopeReady, isClinicianInScope]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return {
    licenses,
    weeklyHours,
    adminOverages,
    dailyHours,
    monthsFullTime,
    monthlyHours,
    noteQuality,
    supervisees,
    providerOptions,
    loading,
    error,
    refetch: doFetch,
  };
}
